<template>
  <div>
    <TransitionRoot appear :show="isOpen" as="template">
      <Dialog as="div" @close="$emit('close')" class="relative z-[100]">
        <!-- Backdrop -->
        <TransitionChild
          as="template"
          enter="ease-in-out duration-300"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="ease-in-out duration-300"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </TransitionChild>

        <!-- Drawer -->
        <div class="fixed inset-0 overflow-hidden">
          <div class="absolute inset-0 overflow-hidden">
            <div class="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
              <TransitionChild
                as="template"
                enter="transform transition ease-in-out duration-300"
                enter-from="translate-x-full"
                enter-to="translate-x-0"
                leave="transform transition ease-in-out duration-300"
                leave-from="translate-x-0"
                leave-to="translate-x-full"
              >
                <DialogPanel class="pointer-events-auto w-screen max-w-[600px]">
                  <div class="flex h-full flex-col overflow-y-auto bg-white shadow-xl">
                    <!-- Header -->
                    <div class="px-6 py-4 border-b border-gray-200 flex justify-between items-center bg-gray-50">
                      <DialogTitle as="h3" class="text-lg font-medium text-gray-900">
                        Generate New Email
                      </DialogTitle>
                      <button @click="$emit('close')" class="text-gray-400 hover:text-gray-500 p-2">
                        <span class="sr-only">Close</span>
                        <XMarkIcon class="h-6 w-6" aria-hidden="true" />
                      </button>
                    </div>

                    <!-- Content -->
                    <div v-if="!showInteractiveEditor" class="flex-1 overflow-y-auto">
                      <div class="p-6 space-y-6">
                        <!-- Article Selection (when type is 'article') -->
                        <div v-if="generationType === 'article'" class="space-y-2">
                          <div class="flex justify-between items-center mb-2">
                            <label class="block text-sm font-medium text-gray-700">Select Article</label>
                            <label class="inline-flex items-center">
                              <input
                                type="checkbox"
                                v-model="showOnlyImportant"
                                class="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                              >
                              <span class="ml-2 text-sm text-gray-600">Show only important</span>
                            </label>
                          </div>
                          <div class="relative">
                            <input
                              v-model="articleSearchQuery"
                              type="text"
                              placeholder="Search articles..."
                              class="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm mb-2"
                              @input="handleArticleSearch"
                            />
                            <select
                              v-model="selectedContentId"
                              class="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            >
                              <option value="">Select an article...</option>
                              <option v-for="content in filteredArticlesComputed" :key="content.id" :value="content.id">
                                {{ content.title }}
                              </option>
                            </select>
                          </div>
                        </div>

                        <!-- Institution Selection -->
                        <div class="space-y-2">
                          <label class="block text-sm font-medium text-gray-700">
                            Select Institution(s) (Optional)
                            <span v-if="selectedInstitutionIds.length > 0" class="text-indigo-600 font-medium">
                              - {{ selectedInstitutionIds.length }} selected, {{ totalSelectedContacts }} contacts
                            </span>
                          </label>
                          <div class="relative">
                            <input
                              v-model="institutionSearchQuery"
                              type="text"
                              placeholder="Search institutions..."
                              class="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm mb-2"
                              @input="handleInstitutionSearch"
                            />
                            <!-- Multi-select list with checkboxes -->
                            <div class="border border-gray-300 rounded-md max-h-64 overflow-y-auto bg-white">
                              <label
                                v-for="institution in filteredInstitutionsComputed"
                                :key="institution.id"
                                class="flex items-start px-3 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                              >
                                <input
                                  type="checkbox"
                                  :value="institution.id"
                                  v-model="selectedInstitutionIds"
                                  class="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 mr-3 mt-0.5"
                                />
                                <div class="flex-1 min-w-0">
                                  <div class="text-sm font-medium text-gray-900">{{ institution.name }}</div>
                                  <div class="text-xs text-gray-500 mt-0.5 flex flex-wrap gap-2">
                                    <span v-if="institution.type" class="inline-flex items-center px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">
                                      {{ institution.type }}
                                    </span>
                                    <span v-if="institution.region" class="inline-flex items-center px-1.5 py-0.5 rounded bg-green-100 text-green-700">
                                      {{ institution.region }}
                                    </span>
                                    <span v-if="institution.language" class="inline-flex items-center px-1.5 py-0.5 rounded bg-purple-100 text-purple-700">
                                      {{ institution.language.toUpperCase() }}
                                    </span>
                                    <span class="text-gray-600">
                                      {{ institution.institution_contacts?.length || 0 }} contacts
                                    </span>
                                  </div>
                                </div>
                              </label>
                              <div v-if="filteredInstitutionsComputed.length === 0" class="px-3 py-4 text-sm text-gray-500 text-center">
                                No institutions found
                              </div>
                            </div>
                          </div>
                        </div>

                        <!-- Generation Mode Selection -->
                        <div class="mb-4">
                          <label class="block text-sm font-medium text-gray-700 mb-2">Generation Mode</label>
                          <div class="space-y-2">
                            <label class="inline-flex items-center">
                              <input
                                type="radio"
                                v-model="generationMode"
                                value="standard"
                                class="form-radio text-indigo-600"
                              />
                              <span class="ml-2 text-sm text-gray-700">Standard (Automatic)</span>
                            </label>
                            <label class="inline-flex items-center">
                              <input
                                type="radio"
                                v-model="generationMode"
                                value="interactive"
                                class="form-radio text-indigo-600"
                              />
                              <span class="ml-2 text-sm text-gray-700">Interactive (Edit before sending)</span>
                            </label>
                          </div>
                        </div>

                        <!-- Generation Mode Switch -->
                        <div class="mt-4 p-3 bg-gray-50 rounded-md">
                          <label class="relative inline-flex items-center cursor-pointer">
                            <input type="checkbox" v-model="generateForAllRecipients" class="sr-only peer">
                            <div class="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                            <span class="ml-3 text-sm font-medium text-gray-700">Generate emails for all recipients</span>
                          </label>
                          
                        </div>

                        <!-- Prompt/Instructions -->
                        <div>
                          <label class="block text-sm font-medium text-gray-700">
                            {{ generationType === 'article' ? 'Instructions for Article-Based Email' : 'Custom Prompt' }}
                          </label>
                          <div v-if="generationType === 'article'" class="mb-2 text-sm text-gray-600 bg-blue-50 p-3 rounded">
                            <p class="font-medium mb-1">How to use instructions effectively:</p>
                            <ul class="list-disc pl-5 space-y-1">
                              <li>Specify the tone you want (formal, friendly, urgent, etc.)</li>
                              <li>Mention specific points from the article to emphasize</li>
                              <li>Include any specific requests or calls to action</li>
                              <li>Example: "Focus on the environmental impact mentioned in paragraph 3. Use a formal tone and request a meeting to discuss solutions."</li>
                            </ul>
                          </div>
                          <textarea
                            v-model="generatePrompt"
                            rows="4"
                            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            :placeholder="generationType === 'article' 
                              ? 'Add any specific instructions for generating the email based on the selected article...'
                              : 'Describe what kind of email you want to generate...'"
                          ></textarea>
                        </div>
                      </div>
                    </div>

                    <!-- Interactive Editor -->
                    <div v-if="showInteractiveEditor && interactiveDraftData" class="flex-1 overflow-y-auto">
                      <EmailInteractiveEditor
                        :initial-draft-data="interactiveDraftData"
                        @approve="handleInteractiveApprove"
                        @cancel="handleInteractiveCancel"
                        @regenerate="handleInteractiveRegenerate"
                      />
                    </div>

                    <!-- Footer Actions -->
                    <div v-if="!showInteractiveEditor" class="border-t border-gray-200 px-6 py-4 bg-gray-50">
                      <div class="flex justify-end gap-4">
                        <button
                          @click="$emit('close')"
                          class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50"
                        >
                          Cancel
                        </button>
                        <button
                          @click="generateEmail"
                          :disabled="generating"
                          class="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md shadow-sm hover:bg-indigo-700 disabled:opacity-50"
                        >
                          {{ generating ? 'Generating...' : 'Generate Email' }}
                        </button>
                      </div>
                    </div>
                  </div>
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </div>
      </Dialog>
    </TransitionRoot>

    <!-- Language Warning Modal -->
    <TransitionRoot appear :show="showLanguageWarning" as="template">
      <Dialog as="div" @close="handleLanguageWarningCancel" class="relative z-[110]">
        <TransitionChild
          as="template"
          enter="ease-out duration-300"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="ease-in duration-200"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <div class="fixed inset-0 bg-black bg-opacity-25" />
        </TransitionChild>

        <div class="fixed inset-0 overflow-y-auto">
          <div class="flex min-h-full items-center justify-center p-4 text-center">
            <TransitionChild
              as="template"
              enter="ease-out duration-300"
              enter-from="opacity-0 scale-95"
              enter-to="opacity-100 scale-100"
              leave="ease-in duration-200"
              leave-from="opacity-100 scale-100"
              leave-to="opacity-0 scale-95"
            >
              <DialogPanel class="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <DialogTitle as="h3" class="text-lg font-medium leading-6 text-gray-900 flex items-center">
                  <svg class="h-6 w-6 text-amber-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  Mixed Languages Detected
                </DialogTitle>
                <div class="mt-4">
                  <p class="text-sm text-gray-600 mb-4">
                    The selected institutions use different languages. All emails will be generated in a single language.
                  </p>

                  <div class="bg-gray-50 rounded-md p-3 mb-4">
                    <p class="text-xs font-medium text-gray-700 mb-2">Languages detected:</p>
                    <ul class="space-y-1">
                      <li v-for="(langInfo, index) in detectedLanguages" :key="index" class="text-sm text-gray-600">
                        <span class="font-medium">{{ langInfo.lang.toUpperCase() }}</span>: {{ langInfo.count }} institution{{ langInfo.count > 1 ? 's' : '' }}
                      </li>
                    </ul>
                  </div>

                  <div class="mb-4">
                    <label class="block text-sm font-medium text-gray-700 mb-2">
                      Choose generation language:
                    </label>
                    <select
                      v-model="selectedLanguageForGeneration"
                      class="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    >
                      <option v-for="langInfo in detectedLanguages" :key="langInfo.lang" :value="langInfo.lang">
                        {{ langInfo.lang.toUpperCase() }} ({{ langInfo.count }} institutions)
                      </option>
                    </select>
                  </div>

                  <p class="text-xs text-gray-500">
                    Note: Greetings will still be personalized for each contact, but the email body will be in {{ selectedLanguageForGeneration.toUpperCase() }}.
                  </p>
                </div>

                <div class="mt-6 flex gap-3 justify-end">
                  <button
                    type="button"
                    class="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
                    @click="handleLanguageWarningCancel"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    class="inline-flex justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
                    @click="handleLanguageWarningConfirm"
                  >
                    Continue with {{ selectedLanguageForGeneration.toUpperCase() }}
                  </button>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </TransitionRoot>

    <!-- Loading Overlay for email generation -->
    <div v-if="generating" class="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-[110]">
      <div class="bg-white rounded-lg shadow-xl p-6 max-w-sm w-full mx-4">
        <div class="flex items-center">
          <svg class="animate-spin -ml-1 mr-3 h-8 w-8 text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <div>
            <p class="text-lg font-medium text-gray-900">Generating Email</p>
            <p class="text-sm text-gray-500">Please wait while we create your email...</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { Dialog, DialogPanel, DialogTitle, TransitionRoot, TransitionChild } from '@headlessui/vue'
import { XMarkIcon } from '@heroicons/vue/24/outline'
import apiClient from '@/api/client'
import { EMAILS, INSTITUTIONS, CONTENT } from '@/api/endpoints'
import type { Institution } from '@/types/institution'
import { useEmailGeneration } from '@/composables/useEmailGeneration'
import { useInstitutions } from '@/composables/useInstitutions'
import EmailInteractiveEditor from './EmailInteractiveEditor.vue'

interface Article {
  id: string
  title: string
  content: string
  created_at: string
  deleted_at: string | null
  is_important: boolean
  activism_relevance?: string
  analysis?: {
    aktivizam_relevantnost?: string
    activism_relevance?: string
    [key: string]: any
  }
  analysis_result?: {
    aktivizam_relevantnost?: string
    activism_relevance?: string
    [key: string]: any
  }
}

const props = defineProps<{
  isOpen: boolean
  generationType: 'prompt' | 'article' | null
}>()

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'success', data: any): void
}>()

const { generateEmail: generateEmailComposable, generating } = useEmailGeneration()
const { institutions, fetchInstitutions } = useInstitutions()

const selectedContentId = ref('')
const selectedInstitutionId = ref('') // Keep for backwards compatibility
const selectedInstitutionIds = ref<string[]>([]) // NEW: For multi-select
const generatePrompt = ref('')
const generateForAllRecipients = ref(false)
const institutionSearchQuery = ref('')
const articleSearchQuery = ref('')
const articles = ref<Article[]>([])
const filteredInstitutions = ref<Institution[]>([])
const filteredArticles = ref<Article[]>([])
const showOnlyImportant = ref(false)
const generationMode = ref('standard')
const interactiveDraftData = ref<{
  subject: string;
  body_paragraphs: string[];
  include_article_url: boolean;
  closing_phrase: string;
  greetings: Record<string, string>;
  recipient_count: number;
  language: string;
} | null>(null)
const showInteractiveEditor = ref(false)
const showLanguageWarning = ref(false)
const detectedLanguages = ref<{lang: string, count: number}[]>([])
const selectedLanguageForGeneration = ref('')

// Computed property for filtered articles
const filteredArticlesComputed = computed(() => {
  let filtered = articles.value

  // Filter by search query if present
  if (articleSearchQuery.value) {
    const searchLower = articleSearchQuery.value.toLowerCase()
    filtered = filtered.filter(article =>
      article.title?.toLowerCase().includes(searchLower)
    )
  }

  // Note: is_important filtering is already done by the API in loadRecentContent()
  // No need to filter again here to avoid flickering

  return filtered
})

// Computed property for filtered institutions
const filteredInstitutionsComputed = computed(() => {
  if (!institutionSearchQuery.value) return institutions.value
  const query = institutionSearchQuery.value.toLowerCase()
  return institutions.value.filter(institution => 
    institution.name.toLowerCase().includes(query)
  )
})

// Computed property to get the number of contacts in the selected institution
const selectedInstitutionContactCount = computed(() => {
  if (!selectedInstitutionId.value) return 0
  const selectedInstitution = institutions.value.find(inst => inst.id === selectedInstitutionId.value)
  return selectedInstitution?.institution_contacts?.length || 0
})

// NEW: Computed property to get total contacts across all selected institutions
const totalSelectedContacts = computed(() => {
  if (selectedInstitutionIds.value.length === 0) return 0
  return selectedInstitutionIds.value.reduce((total, id) => {
    const institution = institutions.value.find(inst => inst.id === id)
    return total + (institution?.institution_contacts?.length || 0)
  }, 0)
})

async function loadInstitutions() {
  try {
    const response = await apiClient.get(INSTITUTIONS.LIST, {
      params: {
        include_contacts: true,
        page_size: 75
      }
    })
    institutions.value = response.data.items
  } catch (error) {
    console.error('Error loading institutions:', error)
  }
}

async function loadRecentContent() {
  try {
    console.log('=== LOADING RECENT CONTENT ===')
    console.log('showOnlyImportant:', showOnlyImportant.value)
    const params: any = {
      page: 1,
      page_size: 500, // Load more to ensure we have enough after filtering
      sort_by: 'created_at',
      sort_order: 'desc'
    }

    // Only add is_important param when checkbox is checked
    if (showOnlyImportant.value) {
      params.is_important = true
      console.log('Added is_important=true to params')
    }

    console.log('Request params:', params)
    console.log('About to call API...')
    const response = await apiClient.get(CONTENT.LIST, { params })
    console.log('✅ API CALL COMPLETED')
    console.log('Response object:', response)
    console.log('Response data:', response.data)
    console.log('API Response - total items from backend:', response.data.total)
    console.log('API Response - items count:', response.data.items?.length)

    if (response.data.items && Array.isArray(response.data.items)) {
      // Store all articles first to check what we're getting
      const allArticles = response.data.items as Article[]
      console.log('All articles received from API:', allArticles.length)

      // Log first few articles to see their is_important status
      if (allArticles.length > 0) {
        console.log('Sample articles (first 3):', allArticles.slice(0, 3).map(a => ({
          id: a.id,
          title: a.title,
          is_important: a.is_important,
          activism_relevance: a.activism_relevance
        })))
      } else {
        console.log('No articles returned from API. This means:')
        console.log('- If is_important=true was sent: No articles marked as important exist in DB')
        console.log('- Check database to confirm articles have is_important field set')
      }

      // Filter out deleted articles and non-high relevance articles
      const filteredArticles = allArticles.filter((article: Article) => {
        if (article.deleted_at) {
          console.log(`Filtered out (deleted): ${article.title}`)
          return false
        }

        // Check relevance - only show high relevance articles
        const analysis = article.analysis || article.analysis_result;
        const relevance = article.activism_relevance ||
                         analysis?.aktivizam_relevantnost ||
                         analysis?.activism_relevance ||
                         'niska';
        const isHighRelevance = relevance.toLowerCase() === 'visoka'

        if (!isHighRelevance) {
          console.log(`Filtered out (low relevance): ${article.title} - relevance: ${relevance}`)
        }

        return isHighRelevance
      });
      console.log('After filtering for high relevance:', filteredArticles.length)

      const sortedArticles = filteredArticles
        .sort((a: Article, b: Article) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
      console.log('After sorting:', sortedArticles)

      articles.value = sortedArticles
      console.log('Final articles in articles:', articles.value)
      console.log('=== LOADING COMPLETE ===')
    } else {
      console.error('Invalid response format:', response.data)
      articles.value = []
    }
  } catch (error) {
    console.error('❌ ERROR IN loadRecentContent:', error)
    console.error('Error type:', error instanceof Error ? error.constructor.name : typeof error)
    console.error('Error message:', error instanceof Error ? error.message : error)
    console.error('Error stack:', error instanceof Error ? error.stack : 'No stack')
    articles.value = []
  }
}

async function handleArticleSearch() {
  try {
    console.log('Searching articles with query:', articleSearchQuery.value)
    const response = await apiClient.get(CONTENT.LIST, {
      params: {
        page: 1,
        page_size: 500, // Load more to ensure we have enough after filtering
        sort_by: 'created_at',
        sort_order: 'desc',
        search: articleSearchQuery.value || undefined
      }
    })
    console.log('Search response:', response.data)
    
    if (response.data.items && Array.isArray(response.data.items)) {
      articles.value = (response.data.items as Article[])
        .filter((article: Article) => {
          if (article.deleted_at) return false;
          
          // Check relevance - only show high relevance articles
          const analysis = article.analysis || article.analysis_result;
          const relevance = article.activism_relevance || 
                           analysis?.aktivizam_relevantnost || 
                           analysis?.activism_relevance || 
                           'niska';
          return relevance.toLowerCase() === 'visoka';
        })
        .sort((a: Article, b: Article) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
      console.log('Filtered search results:', articles.value)
    }
  } catch (error) {
    console.error('Error searching articles:', error)
  }
}

async function handleInstitutionSearch() {
  try {
    const response = await apiClient.get(INSTITUTIONS.LIST, {
      params: {
        include_contacts: true,
        search: institutionSearchQuery.value,
        page_size: 75
      }
    })
    institutions.value = response.data.items
  } catch (error) {
    console.error('Error searching institutions:', error)
  }
}

async function generateEmail() {
  try {
    console.log('=== GENERATE EMAIL CLICKED ===')
    console.log('Generation mode:', generationMode.value)
    console.log('Selected content ID:', selectedContentId.value)
    console.log('Selected institution IDs:', selectedInstitutionIds.value)
    console.log('Generate for all recipients:', generateForAllRecipients.value)
    console.log('Prompt:', generatePrompt.value)

    // Use multiple institutions if selected, otherwise fall back to single for backwards compatibility
    const institutionIdsToUse = selectedInstitutionIds.value.length > 0
      ? selectedInstitutionIds.value
      : (selectedInstitutionId.value ? [selectedInstitutionId.value] : [])

    // Check for language consistency
    if (institutionIdsToUse.length > 1) {
      const selectedInstitutions = institutions.value.filter(inst => institutionIdsToUse.includes(inst.id))
      const languageCounts = selectedInstitutions.reduce((acc, inst) => {
        const lang = inst.language || 'en'
        acc[lang] = (acc[lang] || 0) + 1
        return acc
      }, {} as Record<string, number>)

      // If more than one language detected, show warning
      if (Object.keys(languageCounts).length > 1) {
        detectedLanguages.value = Object.entries(languageCounts)
          .map(([lang, count]) => ({ lang, count }))
          .sort((a, b) => b.count - a.count)

        console.log('Multiple languages detected:', detectedLanguages.value)

        // Set most common language as default
        selectedLanguageForGeneration.value = detectedLanguages.value[0].lang

        // Show warning dialog
        showLanguageWarning.value = true
        return // Wait for user decision
      }
    }

    // Proceed with generation
    await proceedWithGeneration(institutionIdsToUse)
  } catch (error) {
    console.error('=== ERROR GENERATING EMAIL ===')
    console.error('Error:', error)
    console.error('Error response:', (error as any).response)
    console.error('Error response data:', (error as any).response?.data)
    // Handle error...
  }
}

async function proceedWithGeneration(institutionIdsToUse: string[]) {
  try {
    generating.value = true

    const params = {
      prompt: generatePrompt.value,
      content_id: selectedContentId.value,
      institution_ids: institutionIdsToUse,
      generate_for_all: generateForAllRecipients.value,
      use_json_mode: true,
      preferred_language: selectedLanguageForGeneration.value || undefined
    }

    console.log('Request params:', params)

    if (generationMode.value === 'interactive') {
      console.log('Calling interactive endpoint: /api/emails/generate-interactive')

      // Call the interactive endpoint
      const response = await apiClient.post('/api/emails/generate-interactive', params)

      console.log('Response received:', response)
      console.log('Response status:', response.status)
      console.log('Response data:', response.data)
      console.log('Response data status:', response.data?.status)

      if (response.data.status === 'awaiting_edit') {
        console.log('Status is awaiting_edit, setting interactive editor data')
        console.log('Draft data:', response.data.draft_data)

        interactiveDraftData.value = response.data.draft_data
        showInteractiveEditor.value = true

        console.log('interactiveDraftData.value:', interactiveDraftData.value)
        console.log('showInteractiveEditor.value:', showInteractiveEditor.value)
      } else {
        console.warn('Unexpected response status:', response.data.status)
      }
    } else {
      console.log('Standard generation mode')
      // Standard generation
      const response = await apiClient.post(EMAILS.GENERATE, params)
      console.log('Standard generation response:', response.data)
      emit('success', response.data)
      emit('close')
    }
  } catch (error) {
    console.error('=== ERROR GENERATING EMAIL ===')
    console.error('Error:', error)
    console.error('Error response:', (error as any).response)
    console.error('Error response data:', (error as any).response?.data)
    // Handle error...
  } finally {
    console.log('Setting generating to false')
    generating.value = false
  }
}

function handleLanguageWarningConfirm() {
  showLanguageWarning.value = false
  const institutionIdsToUse = selectedInstitutionIds.value.length > 0
    ? selectedInstitutionIds.value
    : (selectedInstitutionId.value ? [selectedInstitutionId.value] : [])
  proceedWithGeneration(institutionIdsToUse)
}

function handleLanguageWarningCancel() {
  showLanguageWarning.value = false
  selectedLanguageForGeneration.value = ''
}

const handleInteractiveApprove = async (draftData: {
  subject: string;
  body_paragraphs: string[];
  include_article_url: boolean;
  closing_phrase: string;
  greetings: Record<string, string>;
  recipient_count: number;
  language: string;
}) => {
  try {
    console.log('=== INTERACTIVE APPROVE CLICKED ===')
    console.log('Draft data:', draftData)

    generating.value = true

    // Use multiple institutions if selected
    const institutionIdsToUse = selectedInstitutionIds.value.length > 0
      ? selectedInstitutionIds.value
      : (selectedInstitutionId.value ? [selectedInstitutionId.value] : [])

    const params = {
      draft_data: draftData,
      content_id: selectedContentId.value,
      institution_ids: institutionIdsToUse, // NEW: Array of institution IDs
      generate_for_all: generateForAllRecipients.value
    }

    console.log('Finalize params:', params)
    console.log('Calling /api/emails/finalize-interactive')

    const response = await apiClient.post('/api/emails/finalize-interactive', params)
    console.log('Finalize response:', response.data)

    emit('success', response.data)
    showInteractiveEditor.value = false
    emit('close')
  } catch (error) {
    console.error('=== ERROR FINALIZING EMAIL ===')
    console.error('Error finalizing email:', error)
    // Handle error...
  } finally {
    generating.value = false
  }
}

const handleInteractiveCancel = () => {
  showInteractiveEditor.value = false
  interactiveDraftData.value = null
}

const handleInteractiveRegenerate = async () => {
  showInteractiveEditor.value = false
  interactiveDraftData.value = null
  await generateEmail()
}

// Load data when drawer opens
watch(() => props.isOpen, async (newValue) => {
  if (newValue) {
    console.log('Drawer opened, loading data...')
    if (props.generationType === 'article') {
      console.log('Loading articles first...')
      await loadRecentContent()
      console.log('Articles loaded, loading institutions...')
    }
    await loadInstitutions()
  } else {
    // Reset form when drawer closes
    generatePrompt.value = ''
    selectedInstitutionId.value = ''
    selectedInstitutionIds.value = [] // NEW: Clear multi-select
    selectedContentId.value = ''
    generateForAllRecipients.value = false
    articleSearchQuery.value = ''
    institutionSearchQuery.value = ''
    articles.value = []
  }
})

// Add a watcher for showOnlyImportant to reload content when it changes
watch(showOnlyImportant, () => {
  if (props.isOpen) {
    loadRecentContent()
  }
})

// Reset state when drawer closes or type changes
// Note: This watcher is removed because it conflicts with the earlier watcher
// that properly calls loadRecentContent() which respects showOnlyImportant

watch(() => props.generationType, () => {
  // Reset fields when type changes
  selectedContentId.value = ''
  generatePrompt.value = ''
})

// Removed fetchInitialData() and fetchArticles() as they're redundant
// The main watcher for props.isOpen at line 584 handles data loading correctly
</script>

<style>
/* Remove any transform styles that might interfere with the transition */
.pointer-events-none {
  pointer-events: none;
}

.pointer-events-auto {
  pointer-events: auto;
}
</style> 